import React, { useEffect, useState } from "react";
import { Box, ButtonBase, Typography } from "@mui/material";
import { C, FONT, eyebrowSx } from "../accountStyles";
import SharedLoadingState from "../../../components/common/LoadingState";

/*
 * Small building blocks shared by every Account screen.
 * Presentation only — no data loading, no navigation of their own.
 */

/** Square status tag (Winning / Outbid / Paid …). tone: "success" | "danger" | "neutral". */
export function StatusTag({ label, tone = "neutral", sx }) {
  const map = {
    success: { color: C.success, bg: C.successBg },
    danger: { color: C.danger, bg: C.dangerBg },
    neutral: { color: C.ink, bg: C.soft },
  };
  const t = map[tone] || map.neutral;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        px: 1,
        py: 0.25,
        fontFamily: FONT,
        fontSize: "0.65rem",
        fontWeight: 700,
        letterSpacing: "1.5px",
        textTransform: "uppercase",
        lineHeight: 1.6,
        color: t.color,
        bgcolor: t.bg,
        border: `1px solid ${t.color}`,
        whiteSpace: "nowrap",
        ...sx,
      }}
    >
      {label}
    </Box>
  );
}


export function ArtworkThumb({ src, alt = "", ratio = "4 / 5", dimmed = false, children, sx }) {
  const [failed, setFailed] = useState(false);
  // A new image URL gets a fresh try (cards are re-used when the page changes).
  useEffect(() => {
    setFailed(false);
  }, [src]);

  return (
    <Box sx={{ position: "relative", width: "100%", aspectRatio: ratio, bgcolor: C.soft, overflow: "hidden", ...sx }}>
      {src && !failed ? (
        <Box
          component="img"
          src={src}
          alt={alt}
          loading="lazy"
          onError={() => setFailed(true)}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            p: { xs: 1, md: 1.5 },
            boxSizing: "border-box",
            filter: dimmed ? "grayscale(0.8)" : "none",
            transition: "transform 0.4s ease",
          }}
        />
      ) : (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ ...eyebrowSx, fontWeight: 400 }}>No image</Typography>
        </Box>
      )}
      {children}
    </Box>
  );
}


export function CardButton({ onClick, children, sx }) {
  return (
    <ButtonBase
      component="div"
      onClick={onClick}
      focusRipple
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        textAlign: "left",
        width: "100%",
        height: "100%",
        bgcolor: C.paper,
        border: `1px solid ${C.line}`,
        borderRadius: 0,
        fontFamily: FONT,
        transition: "border-color 0.2s ease, background-color 0.2s ease",
        "&:hover, &.Mui-focusVisible": { borderColor: C.ink },
        "&:hover img": { transform: "scale(1.02)" },
        "&:hover .acc-title, &.Mui-focusVisible .acc-title": { textDecoration: "underline", textUnderlineOffset: "3px" },
        ...sx,
      }}
    >
      {children}
    </ButtonBase>
  );
}

/** Empty / no-results block. */
export function EmptyState({ title, text, action }) {
  return (
    <Box sx={{ textAlign: "center", py: { xs: 7, md: 10 }, px: 3, border: `1px solid ${C.line}`, bgcolor: C.soft }}>
      <Typography sx={{ fontFamily: FONT, fontSize: { xs: "1.15rem", md: "1.35rem" }, color: C.ink }}>{title}</Typography>
      {text && <Typography sx={{ mt: 1, fontFamily: FONT, color: C.secondary, fontSize: "0.95rem", maxWidth: 440, mx: "auto" }}>{text}</Typography>}
      {action && <Box sx={{ mt: 3 }}>{action}</Box>}
    </Box>
  );
}

/** Backward-compatible account export of the shared loading indicator. */
export function LoadingState(props) {
  return <SharedLoadingState {...props} />;
}
